// Shared helper that slices a full-page canvas into A4 page strips without
// splitting a word, a text line, or an image across two pages.
//
// Usage (after html2canvas captured `element` into `canvas`):
//   const pages = paginateCanvasToPdf({
//     canvas,
//     element,
//     pdfWidth: 210,
//     pageHeightMm: pdf.internal.pageSize.getHeight(),
//   });
//   pages.forEach((p, i) => { if (i > 0) pdf.addPage(); pdf.addImage(p.sliceImgData, "JPEG", x, y, pdfWidth, p.sliceHeightMm); });
//
// This is 100% pixel-based: it inspects the canvas that html2canvas actually
// produced, so breaks are always aligned with the rendered text no matter
// how fonts/layout reflow during capture.

export const sliceCanvas = (sourceCanvas, sYpx, sHeightPx) => {
  const sliceEl = document.createElement("canvas");
  sliceEl.width = sourceCanvas.width;
  sliceEl.height = Math.max(1, sHeightPx);
  const ctx = sliceEl.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, sliceEl.width, sliceEl.height);
  ctx.drawImage(
    sourceCanvas,
    0, sYpx, sourceCanvas.width, sHeightPx, // source rect
    0, 0, sourceCanvas.width, sHeightPx      // dest rect
  );
  return sliceEl;
};

export const paginateCanvasToPdf = ({
  canvas,
  element,
  pdfWidth,
  pageHeightMm,
  minChunkFraction = 0.10,
}) => {
  const pageHeightPx = (pageHeightMm * canvas.width) / pdfWidth;
  const mmPerPx = pdfWidth / canvas.width;
  const ctx = canvas.getContext("2d");

  // Vertical ranges (canvas px) occupied by <img> elements - never cut here.
  const imgRanges = [];
  const rootRect = element.getBoundingClientRect();
  const scaleFactor = canvas.width / element.scrollWidth;
  element.querySelectorAll("img").forEach((img) => {
    const w = img.offsetWidth;
    const h = img.offsetHeight;
    if (w < 2 || h < 2) return; // hidden / not laid out
    const rect = img.getBoundingClientRect();
    const top = (rect.top - rootRect.top) * scaleFactor;
    const bottom = (rect.bottom - rootRect.top) * scaleFactor;
    if (bottom <= 0 || top >= canvas.height) return;
    imgRanges.push([Math.max(0, top), Math.min(canvas.height, bottom)]);
  });

  const inImageRange = (y) => imgRanges.some(([a, b]) => y > a && y < b);

  // How "inky" a horizontal row is: the number of sampled pixels whose
  // luminance deviates noticeably from that row's average colour. Text lines
  // score high; blank gaps (even inside bordered/coloured boxes) score ~0.
  const rowInk = (data, rowOffset, rowStep, samplesPerRow) => {
    let sum = 0;
    for (let x = 0; x < canvas.width; x += rowStep) {
      const idx = rowOffset + x * 4;
      sum += 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
    }
    const avg = sum / samplesPerRow;
    let count = 0;
    for (let x = 0; x < canvas.width; x += rowStep) {
      const idx = rowOffset + x * 4;
      const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
      if (Math.abs(lum - avg) > 30) count++;
    }
    return count;
  };

  // Scan upward from `fromPx` down to `toPx` for the nearest row that is a
  // real blank gap (almost no ink). Reading the band with one getImageData
  // call, then walking it in plain JS.
  const findGapRow = (fromPx, toPx) => {
    const chunkTop = Math.max(0, Math.floor(toPx));
    const chunkBottom = Math.min(canvas.height, Math.ceil(fromPx));
    const chunkHeight = chunkBottom - chunkTop;
    if (chunkHeight < 2) return null;

    const data = ctx.getImageData(0, chunkTop, canvas.width, chunkHeight).data;
    const rowStep = Math.max(1, Math.floor(canvas.width / 300));
    const samplesPerRow = Math.ceil(canvas.width / rowStep);
    const maxInk = Math.max(8, Math.floor(samplesPerRow * 0.03));

    for (let rely = chunkHeight - 1; rely >= 0; rely--) {
      const rowOffset = rely * canvas.width * 4;
      const ink = rowInk(data, rowOffset, rowStep, samplesPerRow);
      if (ink <= maxInk) {
        const row = chunkTop + rely;
        if (!inImageRange(row)) return row;
      }
    }
    return null;
  };

  // Find the row with the least ink in the band, preferring rows closer to
  // the bottom (page end) so content fills each page as much as possible.
  const findMinInkRow = (fromPx, toPx) => {
    const chunkTop = Math.max(0, Math.floor(toPx));
    const chunkBottom = Math.min(canvas.height, Math.ceil(fromPx));
    const chunkHeight = chunkBottom - chunkTop;
    if (chunkHeight < 2) return null;

    const data = ctx.getImageData(0, chunkTop, canvas.width, chunkHeight).data;
    const rowStep = Math.max(1, Math.floor(canvas.width / 300));
    const samplesPerRow = Math.ceil(canvas.width / rowStep);

    let bestRow = null;
    let bestScore = Infinity;
    for (let rely = chunkHeight - 1; rely >= 0; rely--) {
      const row = chunkTop + rely;
      if (inImageRange(row)) continue;
      const rowOffset = rely * canvas.width * 4;
      const ink = rowInk(data, rowOffset, rowStep, samplesPerRow);
      // Score = ink + distance penalty (prefer rows closer to bottom)
      const distFromBottom = (chunkHeight - 1 - rely) / chunkHeight;
      const score = ink + distFromBottom * samplesPerRow * 0.02;
      if (score < bestScore) {
        bestScore = score;
        bestRow = row;
      }
    }
    return bestRow;
  };

  // Return the image range that the current break row falls inside, if any.
  const findImageHit = (pageEndPx) => {
    for (let i = 0; i < imgRanges.length; i++) {
      const [a, b] = imgRanges[i];
      if (pageEndPx > a && pageEndPx < b) return imgRanges[i];
    }
    return null;
  };

  const minChunkPx = pageHeightPx * minChunkFraction;

  const pages = [];
  let consumedPx = 0;

  while (consumedPx < canvas.height - 1) {
    const naiveEndPx = Math.min(consumedPx + pageHeightPx, canvas.height);
    let pageEndPx = naiveEndPx;

    if (naiveEndPx < canvas.height) {
      // Search the whole page (upward from the page bottom to just above the
      // minimum chunk) for the nearest blank gap. A gap can never split a
      // word or line, and it can be anywhere on the page - a little extra
      // padding at the bottom is fine.
      const searchTo = consumedPx + minChunkPx;
      const gapRow = findGapRow(naiveEndPx, searchTo);
      if (gapRow && gapRow > consumedPx) {
        pageEndPx = gapRow;
      } else {
        const minRow = findMinInkRow(naiveEndPx, searchTo);
        if (minRow && minRow > consumedPx) pageEndPx = minRow;
      }
    }

    // Never cut through an image: snap the break to just above it (image
    // starts on next page), or just below it if it fully fits on this page.
    const hit = findImageHit(pageEndPx);
    if (hit) {
      if (hit[0] > consumedPx + minChunkPx) {
        pageEndPx = hit[0];
      } else if (hit[1] <= naiveEndPx && hit[1] > consumedPx) {
        pageEndPx = hit[1];
      }
    }

    if (pageEndPx <= consumedPx) {
      pageEndPx = naiveEndPx;
    }

    const sliceHeightPx = Math.max(1, Math.round(pageEndPx - consumedPx));
    const slice = sliceCanvas(canvas, consumedPx, sliceHeightPx);
    const sliceImgData = slice.toDataURL("image/jpeg", 0.85);
    const sliceHeightMm = sliceHeightPx * mmPerPx;

    pages.push({ sliceImgData, sliceHeightMm });
    consumedPx = pageEndPx;
  }

  return pages;
};
